import { Form, Spinner, Table } from "react-bootstrap";
import {
  formatCategoryCc,
  getCategorySelectValue,
} from "../utils/rally.utils";

const getCategoryAliases = (category) => {
  const value = getCategorySelectValue(category);
  const id = category?._id || category?.id;
  return [value, id, category?.id, category?.key].filter(Boolean);
};

const CategoriesPricingTable = ({
  categories = [],
  selectedIds = [],
  onChange,
  pricing = {},
  onPricingChange,
  pricingErrors = {},
  disabled = false,
  isLoading = false,
  isError = false,
  emptyMessage = "Select one or more types to see matching categories.",
}) => {
  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted">
        <Spinner animation="border" size="sm" />
        Loading categories...
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-danger mb-0">
        Failed to load categories. Please refresh and try again.
      </p>
    );
  }

  if (!categories.length) {
    return <p className="text-muted mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover className="mb-0 align-middle">
        <thead>
          <tr>
            <th style={{ width: 48 }} />
            <th>Category Name</th>
            <th style={{ width: 140 }}>Category CC</th>
            <th style={{ width: 180 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((category) => {
            const id = category._id || category.id;
            const value = getCategorySelectValue(category);
            const aliases = getCategoryAliases(category);
            const checked = selectedIds.some((item) => aliases.includes(item));
            const pricingKey =
              selectedIds.find((item) => aliases.includes(item)) || value;
            const pricingError =
              pricingErrors[pricingKey] ||
              pricingErrors[value] ||
              pricingErrors[id] ||
              pricingErrors[category.key];

            return (
              <tr key={id || value}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    id={`rally-category-${value}`}
                    checked={checked}
                    disabled={disabled || !value}
                    aria-label={`Select ${
                      category.title || category.key || "category"
                    }`}
                    onChange={(e) => {
                      const withoutAliases = selectedIds.filter(
                        (item) => !aliases.includes(item)
                      );
                      if (e.target.checked) {
                        onChange([...withoutAliases, value]);
                        return;
                      }
                      onChange(withoutAliases);
                      onPricingChange?.(value, "");
                    }}
                  />
                </td>
                <td>
                  <label
                    htmlFor={`rally-category-${value}`}
                    className="mb-0 fw-semibold"
                    style={{ cursor: disabled ? "default" : "pointer" }}
                  >
                    {category.title || category.key || "Untitled category"}
                  </label>
                  {category.key ? (
                    <div className="text-muted small">{category.key}</div>
                  ) : null}
                </td>
                <td>{formatCategoryCc(category)}</td>
                <td>
                  <Form.Group className="mb-0">
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        checked
                          ? pricing[pricingKey] ?? pricing[value] ?? ""
                          : ""
                      }
                      onChange={(e) =>
                        onPricingChange?.(value, e.target.value)
                      }
                      placeholder="Amount"
                      disabled={disabled || !checked}
                      isInvalid={checked && !!pricingError}
                    />
                    <Form.Control.Feedback type="invalid">
                      {pricingError}
                    </Form.Control.Feedback>
                  </Form.Group>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default CategoriesPricingTable;
